import request from '../../../util/SuperagentUtils';
import { supplierPayoutReportEndpoints } from '../../../requests/api-endpoints';
import type { ApiDateRange } from './apiDates';

/** One month of payouts. Loose and local — only the fields the skills read. */
export interface PayoutMonthRow {
  /** `yyyy-MM`. */
  group?: string;
  /** Integer cents. */
  totalPayouts?: number;
  payoutsCount?: number;
}

interface PayoutsBreakdownBody {
  groups?: PayoutMonthRow[];
}

export interface PayoutMonthsParams extends ApiDateRange {
  locationId?: string;
}

/**
 * The signed-in supplier's payouts totalled per calendar month, in one request.
 *
 * Only payouts that reached the supplier are counted (`settledOnly`): pending,
 * on-hold and failed payouts are not money paid out, and counting them would
 * rank a month by money the supplier never received. This differs from the
 * Payouts report, which lists every status.
 *
 * The server fills in months with no payouts between the start and end dates,
 * so a quiet month comes back as a zero rather than going missing. Windows over
 * 120 months are rejected with a 400. Dates are `MM/DD/YYYY` — build them with
 * {@link ./apiDates.apiDateRange}.
 */
export const fetchPayoutMonths = async (params: PayoutMonthsParams): Promise<PayoutMonthRow[]> => {
  const response = await request
    .get(supplierPayoutReportEndpoints.breakdown)
    .query({ ...params, groupBy: 'MONTH', sort: 'GROUP', settledOnly: true });
  const groups = (response?.body as PayoutsBreakdownBody | undefined)?.groups;
  return Array.isArray(groups) ? groups : [];
};
